import React, { useMemo } from 'react';
import FilterBuilder from './standalone-filter-components';
import {
  FilterField,
  FilterRow,
  FilterLogic,
  FilterOperation,
} from './standalone-filter-types';
import './standalone-filter-styles.scss';

// Default initial state - exported so parent can use it
export const defaultFilterRows: FilterRow[] = [
  {
    id: 'row-1',
    logic: FilterLogic.AND,
    conditions: [
      {
        id: 'condition-1',
        field: null,
        operation: null,
        value: undefined,
      },
    ],
  },
];

// API/URL shape: { logic, conditions } where conditions have field as string key
function normalizeEspVolumeFilter(
  value:
    | FilterRow[]
    | { logic?: string; conditions?: any[] }
    | null
    | undefined,
  sampleFields: FilterField[],
): FilterRow[] {
  if (!value) return defaultFilterRows;
  if (Array.isArray(value) && value.length > 0) return value;
  if (
    typeof value === 'object' &&
    'conditions' in value &&
    Array.isArray((value as any).conditions)
  ) {
    const apiRow = value as { logic?: string; conditions: any[] };
    const logic = apiRow.logic === 'OR' ? FilterLogic.OR : FilterLogic.AND;
    const conditions = apiRow.conditions.map((c: any, i: number) => {
      const fieldKey = typeof c?.field === 'string' ? c.field : c?.field?.key;
      const field = fieldKey
        ? sampleFields.find((f) => f.key === fieldKey) ?? null
        : null;
      // API sends operation "inbetween" for range; UI uses FilterOperation.IS for min/max percentage
      const operation =
        c?.operation === 'inbetween' || c?.operation === FilterOperation.IS
          ? FilterOperation.IS
          : c?.operation ?? FilterOperation.IS;
      // Normalize condition value: ensure { min, max } are numbers so min/max dropdowns display correctly
      let conditionValue: any = c?.value ?? undefined;
      if (
        conditionValue &&
        typeof conditionValue === 'object' &&
        !Array.isArray(conditionValue) &&
        ('min' in conditionValue || 'max' in conditionValue)
      ) {
        const v = conditionValue as { min?: number; max?: number };
        conditionValue = {
          min:
            v.min !== undefined && v.min !== null ? Number(v.min) : undefined,
          max:
            v.max !== undefined && v.max !== null ? Number(v.max) : undefined,
        };
      }
      return {
        id: `condition-${Date.now()}-${i}`,
        field,
        operation,
        value: conditionValue,
      };
    });
    return [
      {
        id: `row-${Date.now()}`,
        logic,
        conditions:
          conditions.length > 0
            ? conditions
            : [
                {
                  id: `condition-${Date.now()}`,
                  field: null,
                  operation: null,
                  value: undefined,
                },
              ],
      },
    ];
  }
  return defaultFilterRows;
}

// Props for EspAdvanceFilter component
interface EspAdvanceFilterProps {
  value?: FilterRow[] | { logic?: string; conditions?: any[] } | null;
  onChange: (filterRows: FilterRow[]) => void;
  disabled?: boolean;
  hasError?: boolean;
}

// Example usage of the standalone filter component
const EspAdvanceFilter: React.FC<EspAdvanceFilterProps> = ({
  value,
  onChange,
  disabled = false,
  hasError = false,
}) => {
  // Sample fields data - replace with your actual field definitions
  const sampleFields: FilterField[] = [
    {
      id: '1',
      key: 'microsoft',
      label: 'Microsoft',
      // NOTE: For current design we use "is" operation
      // so the UI reads "Where Microsoft is 10% to 60%".
      // The previous "Has" based configuration is kept here
      // for future multi-selection design if needed.
      // operations: [FilterOperation.HAS],
      operations: [FilterOperation.IS],
    },
    {
      id: '2',
      key: 'google',
      label: 'Google',
      operations: [FilterOperation.IS],
    },
    {
      id: '3',
      key: 'others',
      label: 'Other',
      operations: [FilterOperation.IS],
    },
  ];

  const valueOptions = [
    // Kept for future multi-selection design (Maximum / Moderate etc.)
    // Currently the UI uses fixed percentage range (10–60)
    // with two dropdowns instead of these discrete options.
    { key: 'maximum', label: 'Maximum' },
    { key: 'moderate', label: 'Moderate' },
    { key: 'minimum', label: 'Minimum' },
    { key: 'auto', label: 'Auto' },
  ];

  const normalizedFilterRows = useMemo(
    () => normalizeEspVolumeFilter(value, sampleFields),
    [value, sampleFields],
  );

  return (
    <div>
      <FilterBuilder
        fields={sampleFields}
        filterRows={normalizedFilterRows}
        onFilterRowsChange={onChange}
        maxConditions={3}
        className="custom-filter-builder"
        valueOptions={valueOptions}
        disabled={disabled}
        hasError={hasError}
      />
    </div>
  );
};

export default EspAdvanceFilter;
